import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/client/stores/auth';

interface UnifiedAuthContextType {
  isUnifiedAuth: boolean;
  parentPlatform: string | null;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType>({
  isUnifiedAuth: false,
  parentPlatform: null,
});

const trustedOrigins = [
  'https://gigafaze.com',
  'https://www.gigafaze.com',
  'https://app.gigafaze.com',
  "https://dfaze.com",
  "https://www.dfaze.com",
  typeof window !== 'undefined' ? window.location.origin : '',
];

export function UnifiedAuthProvider({ children }: { children: ReactNode }) {
  const [isUnifiedAuth, setIsUnifiedAuth] = useState(false);
  const [parentPlatform, setParentPlatform] = useState<string | null>(null);
  const setUser = useAuthStore((state) => state.setUser);

  const validateUnifiedToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/unified', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success) {
        setIsUnifiedAuth(true);
        setParentPlatform(data.platform);
        setUser(data.user);
        
        console.log('✅ GigaFaze SSO login successful', data.user.email);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Token validation error:', error);
      return false;
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin
      if (!trustedOrigins.includes(event.origin)) {
        console.warn('🚫 Rejected message from untrusted origin:', event.origin);
        return;
      }

      // Handle unified auth token
      if (event.data?.type === 'UNIFIED_AUTH_TOKEN' && event.data?.token) {
        console.log('📨 Received GigaFaze SSO token');
        validateUnifiedToken(event.data.token);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('🎧 Listening for GigaFaze SSO messages');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <UnifiedAuthContext.Provider value={{ isUnifiedAuth, parentPlatform }}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

export const useUnifiedAuth = () => useContext(UnifiedAuthContext);